import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Dialog, Input } from '@/design-system';
import { formatCnpjCpf, formatPhone, unmask, unmaskDocument } from '@/design-system/utils/masks';
import type { CreateAffiliateData } from '../api/affiliates.service';

type AffiliateCreateForm = {
  name: string;
  email: string;
  document: string;
  phone: string;
  commissionPercent: string;
};

interface AffiliateCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  createAffiliate: UseMutationResult<unknown, unknown, CreateAffiliateData, unknown>;
}

const emptyForm = (): AffiliateCreateForm => ({
  name: '',
  email: '',
  document: '',
  phone: '',
  commissionPercent: '',
});

export function AffiliateCreateDialog({ isOpen, onClose, createAffiliate }: AffiliateCreateDialogProps) {
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const reset = () => {
    setFormData(emptyForm());
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) errors.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'E-mail inválido';
    const commission = parseFloat(formData.commissionPercent || '');
    if (formData.commissionPercent && (Number.isNaN(commission) || commission < 0 || commission > 100)) {
      errors.commissionPercent = 'Comissão deve ser entre 0 e 100';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      await createAffiliate.mutateAsync({
        name: formData.name,
        email: formData.email,
        document: unmaskDocument(formData.document) || undefined,
        phone: unmask(formData.phone) || undefined,
        commissionPercent: formData.commissionPercent || undefined,
      });
      handleClose();
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Novo afiliado" size="md" footer={(
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={createAffiliate.isPending}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={() => void handleCreate()} isLoading={createAffiliate.isPending}>
          Criar
        </Button>
      </div>
    )}>
      <div className="space-y-4">
        <Input
          label="Nome"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={formErrors.name}
        />
        <Input
          label="E-mail"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={formErrors.email}
        />
        <Input
          label="CPF/CNPJ (opcional)"
          value={formData.document}
          onChange={(e) => setFormData((prev) => ({ ...prev, document: formatCnpjCpf(e.target.value) }))}
          placeholder="000.000.000-00 ou 00.000.000/0001-00"
        />
        <Input
          label="Telefone (opcional)"
          value={formData.phone}
          onChange={(e) => setFormData((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
          placeholder="(11) 99999-9999"
        />
        <Input
          label="Comissão % (opcional)"
          type="number"
          min={0}
          max={100}
          value={formData.commissionPercent}
          onChange={(e) => {
            const v = e.target.value;
            const num = parseInt(v, 10);
            setFormData((prev) => ({
              ...prev,
              commissionPercent: v === '' ? '' : String(Number.isNaN(num) ? 0 : Math.min(100, Math.max(0, num))),
            }));
          }}
          error={formErrors.commissionPercent}
          placeholder="0 a 100"
        />
      </div>
    </Dialog>
  );
}
